import {
  AppstoreOutlined,
  BarChartOutlined,
  CloudOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyOutlined,
  ShoppingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Spin, Typography } from 'antd';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import logo from '@/assets/Orino.png';
import useLogin from '@/hooks/auth/useLogin';
import { authLoading } from '@/redux/auth/authSlice';
import { useAppDispatch } from '@/redux/hooks';
import toaster from '@/toaster';
import { useState } from 'react';

const { Title, Text } = Typography;

const Login = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [error, setError] = useState<{
    message: string;
    description: string;
  } | null>(null);
  const { loginUser, isPending } = useLogin();

  const onFinish = (values: { email: string; password: string }) => {
    dispatch(authLoading());
    loginUser(values).then(() => {
      toaster.success(`${t('welcome')} 👋`);
      setError(null);
      navigate('/');
    });
  };

  const isArabic = i18n.language === 'ar';

  return (
    <div
      className="h-screen flex items-center justify-center bg-gradient-to-br from-blueSapphire/90 to-frenchBlue p-4"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <Card
        className="w-full max-w-4xl shadow-2xl border-0 overflow-hidden rounded-xl p-0"
        styles={{
          body: {
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <div
          className={`flex flex-col md:flex-row ${isArabic ? 'md:flex-row-reverse' : ''} h-full`}
        >
          {/* Login Form Container */}
          <div className="w-full md:w-1/2 p-5 md:p-8 flex flex-col justify-center">
            <div className="mb-5 text-center md:text-left">
              <img src={logo} alt="ORINO POS" className="h-10 inline-block mb-4" />
              <Title level={3} className="m-0 text-charcoalGrey">
                {t('signIn')}
              </Title>
              <Text className="text-gray-500">
                {t('common:login_subtitle', 'Sign in to access your POS system')}
              </Text>
            </div>

            <Spin spinning={isPending}>
              {error && (
                <Alert
                  message={error.message}
                  type="error"
                  description={error.description}
                  showIcon
                  closable
                  className="mb-4"
                />
              )}

              <Form
                name="login"
                layout="vertical"
                initialValues={{}}
                onFinish={onFinish}
                size="large"
                className="md:max-w-md mx-auto md:mx-0"
              >
                <Form.Item
                  name="email"
                  label={<span className="text-gray-700 font-medium">{t('email')}</span>}
                  rules={[
                    {
                      required: true,
                      message: t('emailRequired'),
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="text-gray-400" />}
                    type="email"
                    placeholder={t('email')}
                    className="h-11 rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span className="text-gray-700 font-medium">{t('password')}</span>}
                  rules={[
                    {
                      required: true,
                      message: t('passwordRequired'),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder={t('password')}
                    className="h-11 rounded-lg"
                  />
                </Form.Item>

                <Form.Item className="mt-6">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="w-full h-11 rounded-lg bg-gradient-to-r from-blueSapphire to-frenchBlue border-0 text-base font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    {t('signIn')}
                  </Button>
                </Form.Item>
              </Form>

              {/* <div className="flex items-center justify-between mt-4 md:max-w-md mx-auto md:mx-0 text-sm">
                <Link to="/forget-password" className="text-frenchBlue hover:text-blueSapphire">
                  {t('forgotPassword')}
                </Link>

                <div>
                  <span className="text-gray-500 mr-1">{t('noAccount')}</span>
                  <Link
                    to="/register"
                    className="text-frenchBlue hover:text-blueSapphire font-medium"
                  >
                    {t('registerHere')}
                  </Link>
                </div>
              </div> */}
            </Spin>

            {/* Mobile Features Section */}
            <div className="md:hidden mt-6 pt-6 border-t border-gray-200">
              <Text className="text-center block mb-3 font-medium">
                {t('common:welcome_to_orino', 'Welcome to ORINO POS')}
              </Text>
              <div className="grid grid-cols-2 gap-2">
                <MobileFeatureItem
                  icon={<AppstoreOutlined />}
                  title={t('common:easy_to_use', 'Easy to Use')}
                />
                <MobileFeatureItem icon={<SafetyOutlined />} title={t('common:secure', 'Secure')} />
                <MobileFeatureItem
                  icon={<BarChartOutlined />}
                  title={t('common:real_time_reporting', 'Reports')}
                />
                <MobileFeatureItem
                  icon={<CloudOutlined />}
                  title={t('common:cloud_based', 'Cloud-based')}
                />
              </div>
            </div>
          </div>

          {/* Desktop Branding Section */}
          <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-blueSapphire to-frenchBlue p-6 flex-col justify-center items-center text-white overflow-auto">
            <div className="max-w-md text-center">
              <img src={logo} alt="Orino POS" className="w-24 h-24 object-contain mb-5" />

              <Title level={3} className="text-white mb-4">
                {t('common:welcome_to_orino', 'Welcome to ORINO POS')}
              </Title>

              <Text className="text-white/80 text-base mb-6">
                {t('common:pos_description', 'A complete point of sale solution for your business')}
              </Text>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <FeatureCard
                  icon={<AppstoreOutlined />}
                  title={t('common:easy_to_use', 'Easy to Use')}
                  description={t('common:intuitive_interface', 'Intuitive interface for all users')}
                />

                <FeatureCard
                  icon={<SafetyOutlined />}
                  title={t('common:secure', 'Secure')}
                  description={t('common:advanced_security', 'Advanced security features')}
                />

                <FeatureCard
                  icon={<BarChartOutlined />}
                  title={t('common:real_time_reporting', 'Real-time Reporting')}
                  description={t(
                    'common:analytics_dashboard',
                    'Detailed analytics and sales insights'
                  )}
                />

                <FeatureCard
                  icon={<ShoppingOutlined />}
                  title={t('common:inventory_management', 'Inventory Management')}
                  description={t('common:stock_tracking', 'Smart stock tracking and alerts')}
                />
              </div>

              <div className="flex justify-between gap-3">
                <FeatureCard
                  icon={<MobileOutlined />}
                  title={t('common:multi_device', 'Multi-device Support')}
                  description={t('common:access_anywhere', 'Access your POS from any device')}
                  className="flex-1"
                />

                <FeatureCard
                  icon={<CloudOutlined />}
                  title={t('common:cloud_based', 'Cloud-based')}
                  description={t('common:data_sync', 'Automatic data synchronization')}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Feature card component to reduce repetition
const FeatureCard = ({
  icon,
  title,
  description,
  className = '',
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}) => (
  <div
    className={`bg-white/10 p-3 rounded-lg backdrop-blur-sm hover:bg-white/15 transition-all ${className}`}
  >
    <div className="text-xl mb-1 text-white">{icon}</div>
    <Title level={5} className="text-white m-0">
      {title}
    </Title>
    <Text className="text-white/80 text-xs">{description}</Text>
  </div>
);

// Simplified feature item for mobile view
const MobileFeatureItem = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <div className="flex items-center bg-gray-50 p-2 rounded-lg">
    <div className="text-frenchBlue text-base mr-2">{icon}</div>
    <span className="text-xs font-medium text-gray-700">{title}</span>
  </div>
);

export default Login;
